import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogFooter } from "@/components/ui/dialog";
import { Crown, Phone } from "@phosphor-icons/react";
import { Spinner } from "@phosphor-icons/react";
import LoyaltyMemberPanel from "@/components/payment/LoyaltyMemberPanel";

/**
 * LOYALTY step of the Pay wizard. Handles the three states: no member
 * yet (phone lookup), unknown phone (enrollment form), or matched
 * member (points + tier perks via LoyaltyMemberPanel). Pure JSX
 * shuffle — all lookup/enrollment API calls stay on PaymentModal.
 */
const LoyaltyStep = ({
  // Member lookup
  loyaltyMember,
  loyaltyPhone, setLoyaltyPhone,
  loyaltyLookupLoading,
  handleLookupPhone,
  // Enrollment
  showEnroll, setShowEnroll,
  enrollName, setEnrollName,
  enrollEmail, setEnrollEmail,
  handleEnroll,
  setLoyaltyMember,
  // Redemption
  subtotal,
  redeemPoints, setRedeemPoints,
  memberPerks,
  selectedPerk, setSelectedPerk,
  // Navigation
  handleDeclineLoyalty,
  onBack,
  onContinue,
}) => (
  <div className="space-y-4 mt-2" data-testid="wizard-loyalty-step">
    {!loyaltyMember && !showEnroll && (
      <>
        <div className="rounded-lg bg-secondary/40 p-4">
          <div className="flex items-center gap-2 mb-1 text-sm font-medium">
            <Crown size={18} weight="fill" className="text-accent" />
            Earn points on this order
          </div>
          <p className="text-xs text-muted-foreground">
            Enter the guest&rsquo;s phone number to look up their loyalty account, or decline to skip.
          </p>
        </div>
        <div className="space-y-2">
          <Label>Phone Number</Label>
          <div className="flex gap-2">
            <Input
              placeholder="(555) 123-4567"
              value={loyaltyPhone}
              onChange={(e) => setLoyaltyPhone(e.target.value)}
              data-testid="loyalty-phone-input"
            />
            <Button
              onClick={handleLookupPhone}
              disabled={loyaltyLookupLoading || loyaltyPhone.replace(/\D/g, "").length < 7}
              data-testid="loyalty-lookup-btn"
            >
              {loyaltyLookupLoading ? <Spinner size={16} className="animate-spin" /> : <Phone size={16} />}
              Look Up
            </Button>
          </div>
        </div>
      </>
    )}

    {showEnroll && !loyaltyMember && (
      <div className="rounded-lg border border-border p-4 space-y-3" data-testid="loyalty-enroll-form">
        <p className="text-sm">No account found. Enroll now?</p>
        <div className="space-y-2">
          <Label>Full Name</Label>
          <Input value={enrollName} onChange={(e) => setEnrollName(e.target.value)} data-testid="loyalty-enroll-name" />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input type="email" value={enrollEmail} onChange={(e) => setEnrollEmail(e.target.value)} data-testid="loyalty-enroll-email" />
        </div>
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={() => { setShowEnroll(false); setLoyaltyMember(null); }}>
            Back
          </Button>
          <Button onClick={handleEnroll} data-testid="loyalty-enroll-submit">Enroll &amp; Continue</Button>
        </div>
      </div>
    )}

    {loyaltyMember && (
      <LoyaltyMemberPanel
        member={loyaltyMember}
        subtotal={subtotal}
        redeemPoints={redeemPoints}
        onRedeemChange={setRedeemPoints}
        perks={memberPerks}
        selectedPerk={selectedPerk}
        onSelectPerk={setSelectedPerk}
      />
    )}

    <DialogFooter className="pt-2 flex !justify-between gap-2 sm:!justify-between">
      <Button variant="ghost" onClick={handleDeclineLoyalty} data-testid="loyalty-decline-btn">
        Decline &amp; Continue
      </Button>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button onClick={onContinue} disabled={!loyaltyMember} data-testid="loyalty-continue-btn">
          Continue to Payment
        </Button>
      </div>
    </DialogFooter>
  </div>
);

export default LoyaltyStep;
